import React, { useRef } from 'react';
import { useInView } from 'framer-motion';

/**
 * SectionReveal — lightweight scroll-triggered fade-up wrapper.
 *
 * Props:
 *  - enabled (boolean)  : when false, renders children immediately with no animation
 *  - delay   (number)   : delay in seconds before the transition starts (default 0)
 *  - className (string) : optional extra classes on the wrapper div
 *
 * Behaviour:
 *  - Uses Framer Motion's useInView (once: true, amount: 0.15)
 *  - Fades in + slides up 24px over 0.7s with easeOut
 *  - After triggering, the element is fully visible and inert (zero ongoing cost)
 */
const SectionReveal = ({ children, enabled = true, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  // If disabled, just render children directly — no wrapper, no overhead
  if (!enabled) return <>{children}</>;

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s ease-out ${delay}s, transform 0.7s ease-out ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

export default SectionReveal;
